/**
 * Biblioteca de utilidades para gerenciamento de gestos em dispositivos móveis
 * Suporta: swipe, tap, double tap, pinch, rotate
 */

// Constantes
const SWIPE_THRESHOLD = 50; // Distância mínima em pixels para considerar um swipe
const SWIPE_VELOCITY = 0.5; // Velocidade mínima para considerar um swipe
const TAP_TIMEOUT = 250; // Tempo máximo em ms para considerar um tap
const DOUBLE_TAP_TIMEOUT = 300; // Tempo máximo entre taps para considerar um double tap

/**
 * Detecta e gerencia gestos de toque para um elemento
 * @param {HTMLElement} element - Elemento HTML para detectar gestos
 * @param {Object} options - Opções de configuração dos gestos
 * @returns {Object} - Objeto com métodos para adicionar/remover gestos
 */
export function createGestureHandler(element, options = {}) {
  if (!element) {
    console.error('Elemento não especificado para createGestureHandler');
    return null;
  }
  
  // Mesclar opções com padrões
  const settings = {
    swipeThreshold: options.swipeThreshold || SWIPE_THRESHOLD,
    swipeVelocity: options.swipeVelocity || SWIPE_VELOCITY,
    tapTimeout: options.tapTimeout || TAP_TIMEOUT,
    doubleTapTimeout: options.doubleTapTimeout || DOUBLE_TAP_TIMEOUT,
    preventDefaultTouchEvents: options.preventDefaultTouchEvents || false,
    disableMouseEvents: options.disableMouseEvents || true, // Desativar para dispositivos desktop por padrão
    passive: options.passive ?? true
  };
  
  // Estado interno de toque
  let touchState = {
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    startTime: 0,
    isMultiTouch: false,
    initialDistance: 0,
    initialAngle: 0,
    lastTapTime: 0,
    touches: []
  };
  
  // Callbacks
  const callbacks = {
    swipe: [],
    swipeleft: [],
    swiperight: [],
    swipeup: [],
    swipedown: [],
    tap: [],
    doubletap: [],
    press: [],
    pinch: [],
    pinchstart: [],
    pinchend: [],
    rotate: [],
    rotatestart: [],
    rotateend: []
  };
  
  // Utilitários internos
  const utils = {
    /**
     * Calcula a distância entre dois pontos
     */
    getDistance: (p1, p2) => {
      return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
    },
    
    /**
     * Calcula o ângulo entre dois pontos
     */
    getAngle: (p1, p2) => {
      return Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
    },
    
    /**
     * Calcula detalhes do gesto de swipe
     */
    getSwipeDetails: () => {
      const deltaX = touchState.endX - touchState.startX;
      const deltaY = touchState.endY - touchState.startY;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      const duration = Date.now() - touchState.startTime;
      const velocity = distance / duration;
      
      // Determinar direção do swipe
      let direction = '';
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        // Movimento horizontal predominante
        direction = deltaX > 0 ? 'right' : 'left';
      } else {
        // Movimento vertical predominante
        direction = deltaY > 0 ? 'down' : 'up';
      }
      
      return {
        deltaX,
        deltaY,
        distance,
        duration,
        velocity,
        direction
      };
    },
    
    /**
     * Extrai as coordenadas de um evento de toque
     */
    getCoordinates: (event) => {
      // Evento de toque
      if (event.touches && event.touches.length > 0) {
        return {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY
        };
      }
      
      // Evento de mouse
      return {
        x: event.clientX,
        y: event.clientY
      };
    },
    
    /**
     * Processa o início de toque
     */
    handleStart: (event) => {
      // Obter coordenadas do toque
      const coords = utils.getCoordinates(event);
      
      // Armazenar estado inicial
      touchState.startX = coords.x;
      touchState.startY = coords.y;
      touchState.startTime = Date.now();
      
      // Verificar se é multi-toque
      touchState.isMultiTouch = event.touches && event.touches.length > 1;
      
      // Se for multi-toque, calcular distância e ângulo iniciais
      if (touchState.isMultiTouch) {
        const touch1 = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        const touch2 = { x: event.touches[1].clientX, y: event.touches[1].clientY };
        
        touchState.initialDistance = utils.getDistance(touch1, touch2);
        touchState.initialAngle = utils.getAngle(touch1, touch2);
        
        // Eventos de início de pinch e rotate
        utils.emit('pinchstart', { distance: touchState.initialDistance });
        utils.emit('rotatestart', { angle: touchState.initialAngle });
      }
      
      // Armazenar todas as posições de toque
      touchState.touches = [];
      if (event.touches) {
        for (let i = 0; i < event.touches.length; i++) {
          touchState.touches.push({
            x: event.touches[i].clientX,
            y: event.touches[i].clientY
          });
        }
      }
      
      // Prevenir comportamento padrão se configurado
      if (settings.preventDefaultTouchEvents) {
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    },
    
    /**
     * Processa o movimento de toque
     */
    handleMove: (event) => {
      // Se for multi-toque, processar gestos de pinch e rotate
      if (event.touches && event.touches.length > 1) {
        const touch1 = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        const touch2 = { x: event.touches[1].clientX, y: event.touches[1].clientY };
        
        // Calcular distância atual para pinch
        const currentDistance = utils.getDistance(touch1, touch2);
        const scale = currentDistance / touchState.initialDistance;
        
        // Emitir evento de pinch
        utils.emit('pinch', {
          scale,
          distance: currentDistance,
          originalDistance: touchState.initialDistance
        });
        
        // Calcular ângulo atual para rotate
        const currentAngle = utils.getAngle(touch1, touch2);
        const rotation = currentAngle - touchState.initialAngle;
        
        // Emitir evento de rotate
        utils.emit('rotate', {
          rotation,
          angle: currentAngle,
          originalAngle: touchState.initialAngle
        });
      }
      
      // Armazenar posição atual
      const coords = utils.getCoordinates(event);
      touchState.endX = coords.x;
      touchState.endY = coords.y;
      
      // Atualizar array de toques
      touchState.touches = [];
      if (event.touches) {
        for (let i = 0; i < event.touches.length; i++) {
          touchState.touches.push({
            x: event.touches[i].clientX,
            y: event.touches[i].clientY
          });
        }
      }
      
      // Prevenir comportamento padrão se configurado
      if (settings.preventDefaultTouchEvents) {
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    },
    
    /**
     * Processa o fim de toque
     */
    handleEnd: (event) => {
      // Obter coordenadas finais
      if (event.changedTouches && event.changedTouches.length > 0) {
        touchState.endX = event.changedTouches[0].clientX;
        touchState.endY = event.changedTouches[0].clientY;
      }
      
      // Processar eventos de término para multi-toque
      if (touchState.isMultiTouch) {
        utils.emit('pinchend');
        utils.emit('rotateend');
      }
      
      // Calcular detalhes do swipe
      const details = utils.getSwipeDetails();
      
      // Verificar se é um swipe
      if (details.distance >= settings.swipeThreshold && 
          details.velocity >= settings.swipeVelocity) {
        // Emitir evento de swipe genérico
        utils.emit('swipe', details);
        
        // Emitir evento de swipe específico da direção
        utils.emit(`swipe${details.direction}`, details);
      } 
      // Verificar se é um tap
      else if (details.distance < 10 && details.duration < settings.tapTimeout) {
        const now = Date.now();
        
        // Verificar se é um double tap
        if (now - touchState.lastTapTime < settings.doubleTapTimeout) {
          utils.emit('doubletap', {
            x: touchState.endX,
            y: touchState.endY
          });
          touchState.lastTapTime = 0; // Resetar para evitar triple-tap
        } else {
          utils.emit('tap', {
            x: touchState.endX,
            y: touchState.endY
          });
          touchState.lastTapTime = now;
        }
      }
      
      // Limpar arrays de toques
      touchState.touches = [];
      touchState.isMultiTouch = false;
      
      // Prevenir comportamento padrão se configurado
      if (settings.preventDefaultTouchEvents) {
        if (event.cancelable) {
          event.preventDefault();
        }
      }
    },
    
    /**
     * Processa o cancelamento de toque
     */
    handleCancel: () => {
      // Emitir eventos de fim para gestos em andamento
      if (touchState.isMultiTouch) {
        utils.emit('pinchend');
        utils.emit('rotateend');
      }
      
      // Limpar estado
      touchState.touches = [];
      touchState.isMultiTouch = false;
    },
    
    /**
     * Emite um evento para todos os callbacks registrados
     */
    emit: (eventName, data = {}) => {
      if (callbacks[eventName]) {
        callbacks[eventName].forEach(callback => {
          try {
            callback({
              ...data,
              target: element,
              type: eventName
            });
          } catch (err) {
            console.error(`Erro no manipulador do evento '${eventName}':`, err);
          }
        });
      }
    }
  };
  
  // Manipuladores de eventos
  const eventHandlers = {
    touchstart: utils.handleStart,
    touchmove: utils.handleMove,
    touchend: utils.handleEnd,
    touchcancel: utils.handleCancel
  };
  
  // Adicionar manipuladores de mouse se não estiverem desativados
  if (!settings.disableMouseEvents) {
    eventHandlers.mousedown = utils.handleStart;
    eventHandlers.mousemove = utils.handleMove;
    eventHandlers.mouseup = utils.handleEnd;
    eventHandlers.mouseleave = utils.handleCancel;
  }
  
  // Adicionar event listeners ao elemento
  Object.entries(eventHandlers).forEach(([event, handler]) => {
    element.addEventListener(event, handler, { 
      passive: settings.passive 
    });
  });
  
  // API pública
  return {
    /**
     * Adiciona um callback para um tipo de gesto
     * @param {string} eventName - Nome do evento de gesto
     * @param {Function} callback - Função de callback
     */
    on: (eventName, callback) => {
      if (callbacks[eventName]) {
        callbacks[eventName].push(callback);
      } else {
        console.warn(`Tipo de gesto não suportado: ${eventName}`);
      }
      
      return {
        remove: () => {
          if (callbacks[eventName]) {
            const index = callbacks[eventName].indexOf(callback);
            if (index !== -1) {
              callbacks[eventName].splice(index, 1);
            }
          }
        }
      };
    },
    
    /**
     * Remove todos os callbacks para um tipo de gesto
     * @param {string} eventName - Nome do evento de gesto (opcional, se omitido remove todos)
     */
    off: (eventName) => {
      if (eventName && callbacks[eventName]) {
        callbacks[eventName] = [];
      } else if (!eventName) {
        // Remover todos
        Object.keys(callbacks).forEach(key => {
          callbacks[key] = [];
        });
      }
    },
    
    /**
     * Remove todos os event listeners e limpa recursos
     */
    destroy: () => {
      // Remover todos os event listeners
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        element.removeEventListener(event, handler);
      });
      
      // Limpar callbacks
      Object.keys(callbacks).forEach(key => {
        callbacks[key] = [];
      });
    },
    
    /**
     * Atualiza as configurações
     * @param {Object} newOptions - Novas opções de configuração
     */
    updateSettings: (newOptions) => {
      Object.assign(settings, newOptions);
    }
  };
}

/**
 * Hook simplificado para detectar swipe em um componente React
 * Uso: const { addSwipeListener, removeSwipeListener } = useSwipe(ref);
 */
export const useSwipe = (elementRef, options = {}) => {
  let gestureInstance = null;
  
  /**
   * Adiciona um listener de swipe ao elemento
   * @param {string} direction - Direção do swipe ('left', 'right', 'up', 'down' ou vazio para qualquer)
   * @param {Function} callback - Função de callback
   */
  const addSwipeListener = (direction, callback) => {
    if (!elementRef.current) {
      console.warn('Elemento de referência não disponível para useSwipe');
      return null;
    }
    
    // Criar instância de gestos se não existir
    if (!gestureInstance) {
      gestureInstance = createGestureHandler(elementRef.current, options);
    }
    
    // Determinar o evento a ser ouvido
    const eventName = direction ? `swipe${direction}` : 'swipe';
    
    // Adicionar listener
    return gestureInstance.on(eventName, callback);
  };
  
  /**
   * Remove todos os listeners de swipe do elemento
   */
  const removeSwipeListener = () => {
    if (gestureInstance) {
      gestureInstance.destroy();
      gestureInstance = null;
    }
  };
  
  return {
    addSwipeListener,
    removeSwipeListener
  };
};

/**
 * Função para habilitar o efeito ripple em elementos (feedback visual de toque)
 * @param {HTMLElement} element - Elemento para adicionar o efeito
 * @param {Object} options - Opções de configuração
 */
export function enableRippleEffect(element, options = {}) {
  if (!element) return null;
  
  // Mesclar opções com padrões
  const settings = {
    color: options.color || 'rgba(255, 255, 255, 0.4)',
    duration: options.duration || 800,
    size: options.size || 'contained', // 'contained' ou 'fixed'
    fixedSize: options.fixedSize || 100,
    removeAfterAnimation: options.removeAfterAnimation ?? true,
    centered: options.centered || false
  };
  
  // Garantir que o elemento tenha posição relativa
  const computedStyle = window.getComputedStyle(element);
  if (computedStyle.position === 'static') {
    element.style.position = 'relative';
  }
  
  // Garantir que o elemento tenha overflow hidden
  element.style.overflow = 'hidden';
  
  // Manipulador de toque/clique
  const handleRipple = (event) => {
    // Criar elemento ripple
    const ripple = document.createElement('span');
    
    // Posicionar o ripple
    let x, y;
    
    if (settings.centered) {
      // Centralizar o ripple
      x = element.offsetWidth / 2;
      y = element.offsetHeight / 2;
    } else {
      // Posicionar no ponto de toque
      const rect = element.getBoundingClientRect();
      
      if (event.type === 'touchstart' && event.touches && event.touches.length > 0) {
        x = event.touches[0].clientX - rect.left;
        y = event.touches[0].clientY - rect.top;
      } else {
        x = event.clientX - rect.left;
        y = event.clientY - rect.top;
      }
    }
    
    // Calcular tamanho do ripple
    let size;
    if (settings.size === 'fixed') {
      size = settings.fixedSize;
    } else {
      // Usar o maior lado do elemento
      const width = element.offsetWidth;
      const height = element.offsetHeight;
      const maxDimension = Math.max(width, height);
      
      // Considerar a distância do ponto de toque até o canto mais distante
      const distX = Math.max(x, width - x);
      const distY = Math.max(y, height - y);
      const hypotenuse = Math.sqrt(distX * distX + distY * distY);
      
      // O ripple precisa ser pelo menos 2x o tamanho da hipotenusa para cobrir todo o elemento
      size = Math.max(maxDimension, hypotenuse * 2);
    }
    
    // Criar estilos para o ripple
    ripple.className = 'mobile-ripple-effect';
    ripple.style.position = 'absolute';
    ripple.style.width = `${size}px`;
    ripple.style.height = `${size}px`;
    ripple.style.left = `${x - size/2}px`;
    ripple.style.top = `${y - size/2}px`;
    ripple.style.backgroundColor = settings.color;
    ripple.style.borderRadius = '50%';
    ripple.style.transform = 'scale(0)';
    ripple.style.animation = `ripple ${settings.duration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
    ripple.style.pointerEvents = 'none';
    
    // Adicionar regra de animação se não existir
    if (!document.querySelector('style#ripple-animation')) {
      const style = document.createElement('style');
      style.id = 'ripple-animation';
      style.textContent = `
        @keyframes ripple {
          from { transform: scale(0); opacity: 1; }
          to { transform: scale(1); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Adicionar ripple ao elemento
    element.appendChild(ripple);
    
    // Remover ripple após a animação
    if (settings.removeAfterAnimation) {
      setTimeout(() => {
        if (ripple && ripple.parentNode) {
          ripple.parentNode.removeChild(ripple);
        }
      }, settings.duration);
    }
  };
  
  // Adicionar event listeners
  element.addEventListener('touchstart', handleRipple, { passive: true });
  element.addEventListener('mousedown', handleRipple);
  
  // API pública para gerenciar o efeito
  return {
    // Atualizar configurações
    updateSettings: (newOptions) => {
      Object.assign(settings, newOptions);
    },
    
    // Destruir e remover event listeners
    destroy: () => {
      element.removeEventListener('touchstart', handleRipple);
      element.removeEventListener('mousedown', handleRipple);
      
      // Remover ripples existentes
      const ripples = element.querySelectorAll('.mobile-ripple-effect');
      ripples.forEach(ripple => {
        if (ripple.parentNode) {
          ripple.parentNode.removeChild(ripple);
        }
      });
    }
  };
}

export default {
  createGestureHandler,
  useSwipe,
  enableRippleEffect
}; 